import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Alert, Button, Modal, Table, Tag, notification, Flex, Typography, Card } from "antd";
import { getInvoices, initiateInvoicePayment } from "../../../service";
import { axiosInstance, PATH } from "../../../service/client";
import { RegulaminContent, PolitykaPrywatnosciContent } from "../legal-documents/LegalDocuments";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface Invoice {
    id: number;
    createdAt: string;
    periodLabel: string;
    amountNet: number;
    amountGross: number;
    ordersCount: number;
    freeOrders: number;
    billableOrders: number;
    orderPrice: number;
    minMonthlyPrice: number;
    status: "PENDING" | "PAID" | "OVERDUE";
    dueDate: string;
    paidAt: string | null;
    paymentUrl: string | null;
    fvViewUrl: string | null;
}

const statusConfig: Record<string, { color: string; labelKey: string }> = {
    PENDING: { color: "orange", labelKey: "billingSettings.statusPending" },
    PAID: { color: "green", labelKey: "billingSettings.statusPaid" },
    OVERDUE: { color: "red", labelKey: "billingSettings.statusOverdue" },
};

export const BillingSettings: React.FC = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [docModal, setDocModal] = useState<{ open: boolean; type: "regulamin" | "polityka" | null }>({
        open: false, type: null,
    });

    const openDocModal = (type: "regulamin" | "polityka") => {
        setDocModal({ open: true, type });
    };

    const downloadAgreementPdf = async () => {
        try {
            const response = await axiosInstance.get(
                `${PATH}/v1/common_api/admin/online-ordering/agreement/pdf`
            );
            if (response.data?.url) {
                window.open(response.data.url, "_blank");
            }
        } catch {
            notification.error({
                message: t("billingSettings.downloadAgreementError"),
                placement: "bottomRight",
            });
        }
    };

    const { data: invoices, isLoading } = useQuery<Invoice[]>(
        ["/admin/invoices"],
        () => getInvoices()
    );

    const { mutateAsync: payInvoice, isLoading: payLoading } = useMutation(
        (invoiceId: number) => initiateInvoicePayment(invoiceId),
        {
            onSuccess: (data) => {
                if (data?.paymentUrl) {
                    window.location.href = data.paymentUrl;
                } else {
                    notification.error({
                        message: t("billingSettings.paymentLinkError"),
                        placement: "bottomRight",
                    });
                }
            },
            onError: () => {
                notification.error({
                    message: t("billingSettings.paymentInitError"),
                    placement: "bottomRight",
                });
            },
        }
    );

    const hasOverdue = invoices?.some((inv) => inv.status === "OVERDUE");

    const columns = [
        {
            title: t("billingSettings.columnPeriod"),
            dataIndex: "periodLabel",
            key: "periodLabel",
            render: (text: string) => <Text strong>{text}</Text>,
        },
        {
            title: t("billingSettings.columnAmountGross"),
            dataIndex: "amountGross",
            key: "amountGross",
            render: (val: number) => `${(val / 100).toFixed(2)} zł`,
        },
        {
            title: t("billingSettings.columnOrders"),
            key: "orders",
            render: (_: any, record: Invoice) => (
                <span>
                    {t("billingSettings.ordersValue", { count: record.ordersCount, billable: record.billableOrders })}
                </span>
            ),
        },
        {
            title: t("billingSettings.columnStatus"),
            dataIndex: "status",
            key: "status",
            render: (status: string) => {
                const conf = statusConfig[status];
                return conf
                    ? <Tag color={conf.color}>{t(conf.labelKey)}</Tag>
                    : <Tag color="default">{status}</Tag>;
            },
        },
        {
            title: t("billingSettings.columnDueDate"),
            dataIndex: "dueDate",
            key: "dueDate",
            render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
        },
        {
            title: t("billingSettings.columnPaidAt"),
            dataIndex: "paidAt",
            key: "paidAt",
            render: (date: string | null) =>
                date ? dayjs(date).format("DD.MM.YYYY HH:mm") : "-",
        },
        {
            title: t("billingSettings.columnInvoice"),
            key: "fv",
            render: (_: any, record: Invoice) => {
                if (record.fvViewUrl) {
                    return (
                        <a href={record.fvViewUrl} target="_blank" rel="noopener noreferrer">
                            {t("billingSettings.viewInvoice")}
                        </a>
                    );
                }
                return record.status === "PAID" ? t("billingSettings.invoiceGenerating") : "-";
            },
        },
        {
            title: t("billingSettings.columnActions"),
            key: "actions",
            render: (_: any, record: Invoice) => {
                if (record.status === "PAID") return null;
                return (
                    <Button
                        type="primary"
                        danger={record.status === "OVERDUE"}
                        loading={payLoading}
                        onClick={() => payInvoice(record.id)}
                    >
                        {record.status === "OVERDUE" ? t("billingSettings.payNow") : t("billingSettings.pay")}
                    </Button>
                );
            },
        },
    ];

    return (
        <Flex vertical gap={16}>
            <Title level={4} style={{ margin: 0 }}>
                {t("billingSettings.pageTitle")}
            </Title>

            {hasOverdue && (
                <Alert
                    message={t("billingSettings.overdueAlertMessage")}
                    description={t("billingSettings.overdueAlertDescription")}
                    type="error"
                    showIcon
                    style={{ marginBottom: 8 }}
                />
            )}

            <Card size="small">
                <Table
                    dataSource={invoices || []}
                    columns={columns}
                    loading={isLoading}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    locale={{
                        emptyText: t("billingSettings.emptyText"),
                    }}
                    size="small"
                />
            </Card>

            <Flex gap={16} wrap="wrap" style={{ marginTop: 4 }}>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "var(--color-info-bg, rgba(24, 144, 255, 0.15))",
                    borderRadius: 6,
                    border: "1px solid var(--color-border-light, #e9ecef)",
                    flex: "1 1 auto",
                }}>
                    <span style={{ fontSize: 16 }}>💳</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {t("billingSettings.paymentsProcessedVia")}{" "}
                        <a href="https://www.przelewy24.pl" target="_blank" rel="noopener noreferrer">
                            <strong>Przelewy24</strong>
                        </a>
                        {t("billingSettings.paymentsAcceptPrefix")}{" "}
                        <a href="https://www.przelewy24.pl/regulamin" target="_blank" rel="noopener noreferrer">
                            {t("billingSettings.p24Terms")}
                        </a>{" "}
                        {t("billingSettings.and")}{" "}
                        <a href="https://www.przelewy24.pl/obowiazek-informacyjny-rodo-platnicy" target="_blank" rel="noopener noreferrer">
                            {t("billingSettings.rodoInfo")}
                        </a>.
                    </Text>
                </div>

                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "var(--color-success-bg, rgba(82, 196, 26, 0.15))",
                    borderRadius: 6,
                    border: "1px solid var(--color-border-light, #e9ecef)",
                }}>
                    <Text style={{ fontSize: 12 }}>
                        📄{" "}
                        <a
                            href="#umowa"
                            onClick={(e) => {
                                e.preventDefault();
                                downloadAgreementPdf();
                            }}
                        >
                            {t("billingSettings.downloadAgreement")}
                        </a>
                        {" | "}
                        <a
                            href="#regulamin"
                            onClick={(e) => {
                                e.preventDefault();
                                openDocModal("regulamin");
                            }}
                        >
                            {t("billingSettings.terms")}
                        </a>
                        {" | "}
                        <a
                            href="#polityka"
                            onClick={(e) => {
                                e.preventDefault();
                                openDocModal("polityka");
                            }}
                        >
                            {t("billingSettings.privacyPolicy")}
                        </a>
                    </Text>
                </div>
            </Flex>

            {/* Document viewer modal */}
            <Modal
                title={docModal.type === "regulamin" ? t("billingSettings.modalTermsTitle") : t("billingSettings.modalPrivacyTitle")}
                open={docModal.open}
                onCancel={() => setDocModal({ open: false, type: null })}
                footer={null}
                width={800}
                styles={{ body: { maxHeight: "70vh", overflowY: "auto", padding: "16px 24px" } }}
            >
                {docModal.type === "regulamin" && <RegulaminContent />}
                {docModal.type === "polityka" && <PolitykaPrywatnosciContent />}
            </Modal>
        </Flex>
    );
};
